///
/// EarthBrowser
///
/// Created by Alex Giger on 7/3/2024
/// Copyright (c) 2024 Geodata Labs
///

import { wgs } from "@/earthbrowser/utils/geographic"
import { type Geometry } from "@stovokor/shader/Geometry"
import { cube } from "@stovokor/math/cube"
import { trig } from "@stovokor/math/math"
import { sphere } from "@stovokor/math/sphere"
import { DrawMode } from "@stovokor/shader/GLConstants"
import { VertexAttribute } from "@stovokor/shader/VertexAttribute"
import { IndexBuffer } from "@stovokor/shader/IndexBuffer"
import { VertexBuffer, VertexBufferUint8 } from "@stovokor/shader/VertexBuffer"
import { cellToChildren, cellToLatLng, cellToVertexes, getRes0Cells, getResolution, type H3Index, vertexToLatLng } from "h3-js"
import { type StovokorContext } from "@stovokor/StovokorContext"

/**
 * Global H3 hex mesh: one center vertex per cell plus shared corner vertices,
 * triangulated as fans. Vertex order matches shared-types/hex-grid-mapping so
 * the per-frame value bytes from the ingest upload straight into the value
 * attributes.
 *
 * Two value streams (current and next frame) feed the shader's crossfade.
 * The index buffer is rebuilt per frame pair to hold only the cells with a
 * signal in either frame, so the aurora ovals cost a fraction of the full
 * 41k-cell globe to draw.
 */
export class HexGridGeometry implements Geometry {
	public mode: DrawMode = DrawMode.Triangles
	public position: VertexAttribute
	public value: VertexAttribute
	public nextValue: VertexAttribute
	public indexBuffer: IndexBuffer
	public vertexBuffer: VertexBuffer
	public valueBuffer: VertexBufferUint8
	public nextValueBuffer: VertexBufferUint8
	public available: boolean = true
	public bounds: cube
	public boundingSphere: sphere

	public vertexCount: number = 0
	public cellToVertexIndices = new Map<H3Index, number[]>()

	// Flattened per-cell layout for the active-cell scan: vertex indices of
	// cell c are cellVertices[cellVertexOffsets[c] .. cellVertexOffsets[c+1])
	// and its fan triangles are fullIndices[cellIndexOffsets[c] .. cellIndexOffsets[c+1])
	private cellVertices: Uint32Array
	private cellVertexOffsets: Uint32Array
	private fullIndices: Uint32Array
	private cellIndexOffsets: Uint32Array
	private scratchIndices: Uint32Array
	private cellCount: number
	// 1 for cells with vertices in both hemispheres. The field bake maps
	// each hemisphere to its own disc, so a triangle spanning the equator
	// would streak across the whole texture; those cells are never drawn
	// (the ingest leaves the equatorial band at 0 anyway)
	private cellStraddles: Uint8Array

	constructor(context: StovokorContext, resolution: number) {
		this.position = new VertexAttribute(context, 2)
		this.value = new VertexAttribute(context, 1)
		this.nextValue = new VertexAttribute(context, 1)
		this.vertexBuffer = new VertexBuffer(context, [this.position])
		this.valueBuffer = new VertexBufferUint8(context, [this.value])
		this.nextValueBuffer = new VertexBufferUint8(context, [this.nextValue])

		const rad = trig.rad
		const res0 = getRes0Cells()
		const cells = res0.flatMap(index => this.getChildCells(index, resolution))

		let vertexIndex = 0
		const vertexMap = new Map<H3Index, number>()
		const cellVertices: number[] = []
		const cellVertexOffsets: number[] = [0]
		const indices: number[] = []
		const cellIndexOffsets: number[] = [0]
		const straddles: number[] = []

		// Build a set of vertex indices for each cell including the cell center vertex
		// and the vertices of the corners in order to build a triangle set for each cell
		// Use a map to store the vertex index for each vertex to avoid duplicates
		for(const cell of cells) {
			const cellVertexIndices: number[] = []
			const cellLatLng = cellToLatLng(cell)
			this.position.add(cellLatLng[1] * rad, cellLatLng[0] * rad)
			this.value.add(0)
			this.nextValue.add(0)
			cellVertexIndices.push(vertexIndex)
			vertexMap.set(cell, vertexIndex++)
			let minLat = cellLatLng[0]
			let maxLat = cellLatLng[0]

			const vertices = cellToVertexes(cell)
			for(const vertex of vertices) {
				const vertexLatLng = vertexToLatLng(vertex)
				minLat = Math.min(minLat, vertexLatLng[0])
				maxLat = Math.max(maxLat, vertexLatLng[0])
				const index = vertexMap.get(vertex)
				if(index !== undefined) {
					cellVertexIndices.push(index)
				} else {
					this.position.add(vertexLatLng[1] * rad, vertexLatLng[0] * rad)
					this.value.add(0)
					this.nextValue.add(0)
					cellVertexIndices.push(vertexIndex)
					vertexMap.set(vertex, vertexIndex++)
				}
			}
			// this is the final set of vertex indices for the cell
			this.cellToVertexIndices.set(cell, cellVertexIndices)
			cellVertices.push(...cellVertexIndices)
			cellVertexOffsets.push(cellVertices.length)
			straddles.push(minLat < 0 && maxLat >= 0 ? 1 : 0)

			// Triangle fan around the center vertex, closed back to the first corner
			const corners = cellVertexIndices.length
			for (let i = 1; i < corners - 1; i++) {
				indices.push(cellVertexIndices[0], cellVertexIndices[i], cellVertexIndices[i + 1])
			}
			indices.push(cellVertexIndices[0], cellVertexIndices[corners - 1], cellVertexIndices[1])
			cellIndexOffsets.push(indices.length)
		}

		this.vertexCount = vertexIndex
		this.cellCount = cells.length
		this.cellVertices = Uint32Array.from(cellVertices)
		this.cellVertexOffsets = Uint32Array.from(cellVertexOffsets)
		this.fullIndices = Uint32Array.from(indices)
		this.cellIndexOffsets = Uint32Array.from(cellIndexOffsets)
		this.cellStraddles = Uint8Array.from(straddles)
		this.scratchIndices = new Uint32Array(this.fullIndices.length)

		// Nothing is drawn until a frame selects its active cells
		this.indexBuffer = new IndexBuffer(context, new Uint32Array(0))

		const radius = wgs.a + 10
		this.bounds = new cube(-radius, radius)
		this.boundingSphere = new sphere(radius)
	}

	/**
	 * Rebuild the index buffer from the cells that carry a signal in either
	 * of the two frames being blended. Returns the number of active cells.
	 */
	public setActiveCells(current: Uint8Array, next: Uint8Array): number {
		const out = this.scratchIndices
		let written = 0
		let activeCells = 0
		for (let c = 0; c < this.cellCount; c++) {
			if (this.cellStraddles[c]) continue
			const vEnd = this.cellVertexOffsets[c + 1]
			let active = false
			for (let k = this.cellVertexOffsets[c]; k < vEnd; k++) {
				const v = this.cellVertices[k]
				if (current[v] > 0 || next[v] > 0) {
					active = true
					break
				}
			}
			if (!active) continue
			const iStart = this.cellIndexOffsets[c]
			const iEnd = this.cellIndexOffsets[c + 1]
			out.set(this.fullIndices.subarray(iStart, iEnd), written)
			written += iEnd - iStart
			activeCells++
		}
		this.indexBuffer.uploadData(out.slice(0, written))
		return activeCells
	}

	private getChildCells(index: H3Index, res: number): H3Index[] {
		if(getResolution(index) === res) {
			return [index]
		}
		const children = cellToChildren(index, res)
		if(getResolution(index) >= res - 1) {
			return children
		}
		return children.flatMap(child => this.getChildCells(child, res))
	}

	public destroy(): void {
		this.vertexBuffer.destroy()
		this.indexBuffer.destroy()
		this.valueBuffer.destroy()
		this.nextValueBuffer.destroy()
	}
}
